import { useEffect, useState } from "react";
import Loader from "../../utils/Loading";
import machineService from "../../../services/MachineService";
import { Grid, IconButton } from "@mui/material";
import styles from "./styles.module.css";
import AddLabelScalePopup from "../../popups/AddLabelScaleDialog/AddLabelScalePopup";
import WarningMsg from "../../utils/WarningMsg/WarningMsg";
import ButtonRed from "../../utils/CustomFields/ButtonRed";
import { ReactComponent as PrinterIcon } from "../../../images/svg/printer_light_gray.svg";
import { ReactComponent as MonitorIcon } from "../../../images/svg/monitor.svg";
import { ReactComponent as TrashIcon } from "../../../images/svg/trash.svg";
import { ReactComponent as EditIcon } from "../../../images/svg/edit.svg";
import AppBridgeHelper from "../../../helpers/AppBridgeHelper";
import { toast } from "react-hot-toast";
import { MACHINE_STATUS, UNKNOWN_IP } from "const";
import ConfirmPopup from "components/popups/Alert/ConfirmPopup";

function LabelScale() {
  const [loading, setLoading] = useState(false);
  const [machines, setMachines] = useState([]);
  const [addMachine, setAddMachine] = useState(false);
  const [editingMachine, setEditingMachine] = useState(null);
  const [machineToDelete, setMachineToDelete] = useState(null);
  const [isServerRunning, setIsServerRunning] = useState(false);
  const [serverIp, setServerIp] = useState("");
  const [onlineMacs, setOnlineMacs] = useState([]);

  const closeAddMachineDialogHandler = () => {
    setAddMachine(false);
    setEditingMachine(null);
    getMachines();
  };

  const openAddMachineDialogHandler = () => {
    setEditingMachine(null);
    setAddMachine(true);
  };

  const handleEditMachine = (machine) => {
    setEditingMachine(machine);
    setAddMachine(true);
  };

  const getMachines = async () => {
    setLoading(true);
    try {
      const resp = await machineService.getMachines();
      setMachines(resp);
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  const checkServerStatus = async () => {
    const bridge = AppBridgeHelper.getBridge();
    if (bridge) {
      if (bridge.isScaleServerRunning) {
        try {
          const running = await bridge.isScaleServerRunning();
          setIsServerRunning(running === "true");
        } catch (e) {
          console.error(e);
        }
      }
      if (bridge.getLocalIp) {
        try {
          const ip = await bridge.getLocalIp();
          if (ip) setServerIp(ip);
        } catch (e) {
          console.error(e);
        }
      }
    }
  };

  const checkOnlineMachines = async () => {
    const bridge = AppBridgeHelper.getBridge();
    if (bridge && bridge.getOnlineMachines) {
      const macsString = await bridge.getOnlineMachines();
      if (macsString) {
        try {
          setOnlineMacs(JSON.parse(macsString));
        } catch (e) {
          console.error(e);
        }
      }
    }
  };

  const handleLabelServer = async () => {
    const bridge = AppBridgeHelper.getBridge();
    if (!bridge) {
      toast.error("Bridge not found. Only runs on desktop app.");
      return;
    }

    setLoading(true);
    try {
      if (isServerRunning) {
        if (bridge.stopScaleServer) {
          await bridge.stopScaleServer();
          toast.success("Scale server stopped");
        }
      } else {
        if (bridge.startScaleServer) {
          const res = await bridge.startScaleServer();
          if (res && res.startsWith("ERROR")) {
            toast.error(res);
          } else {
            toast.success("Scale server started");
          }
        }
      }
      await checkServerStatus();
    } catch (e) {
      toast.error("Failed to toggle server status");
    }
    setLoading(false);
  };

  const normalizeMac = (mac) => (mac || "").replace(/[:-]/g, "").trim().toUpperCase();

  const handleSyncMachine = async (machine) => {
    const bridge = AppBridgeHelper.getBridge();
    if (bridge && bridge.syncProductsToMachine && bridge.syncBillFormatToMachine) {
      setLoading(true);
      try {
        await machineService.syncToNative();
        await bridge.syncProductsToMachine(machine.mac_id);
        await bridge.syncBillFormatToMachine(machine.mac_id);
        toast.success(`Sent sync commands to ${machine.machine_name}`);
      } catch (e) {
        toast.error("Failed to sync: " + e.message);
      }
      setLoading(false);
    } else {
      toast.error("Desktop bridge sync helpers not found");
    }
  };

  const handleDeleteMachine = (machine) => {
    setMachineToDelete(machine);
  };

  const onConfirmDeleteMachine = async (machine) => {
    if (!machine) return;
    setLoading(true);
    try {
      await machineService.deleteMachine(machine.id);
      toast.success("Machine deleted");
      await getMachines();
    } catch (e) {
      toast.error("Failed to delete machine");
    }
    setLoading(false);
  };

  const StatusBadge = ({ status }) => (
    <span className={`${styles.statusBadge} ${status === true ? styles.online : styles.offline}`}>
      {status ? MACHINE_STATUS.ONLINE : MACHINE_STATUS.OFFLINE}
    </span>
  );

  useEffect(() => {
    const fetchIp = async () => {
      const bridge = AppBridgeHelper.getBridge();
      if (!bridge) {
        setServerIp(UNKNOWN_IP);
        return;
      }
      try {
        const ip = await bridge.getLocalIp();
        setServerIp(ip);
      } catch (e) {
        console.error(e);
      }
    };
    fetchIp();
  }, []);

  useEffect(() => {
    getMachines();
    checkServerStatus();
    checkOnlineMachines();
    machineService.syncToNative();

    const interval = setInterval(() => {
      checkOnlineMachines();
      checkServerStatus();
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <Loader isOpen={loading} />
      <div className={`${styles.container} ${styles.borderBottom}`}>
        <div className={styles.serverContainer}>
          <Grid className={styles.serverInfo}>
            <PrinterIcon />
            <span className={styles.label}>Server IP :</span>
            <span className={styles.boldTxt}>{serverIp}</span>
          </Grid>
          <Grid>
            <span className={isServerRunning ? styles.runningText : styles.stoppedText}>
              Scale server {isServerRunning ? "running" : "stopped"}
            </span>
          </Grid>
          <ButtonRed onClick={handleLabelServer}>{isServerRunning ? "Stop Server" : "Start Server"}</ButtonRed>
        </div>
      </div>

      <div className={styles.container}>
        <Grid container className={styles.contentWrapper}>
          <Grid item xs={12}>
            <Grid container justifyContent="space-between" alignItems="center" className={styles.sectionHeader}>
              <span className={styles.boldTxt}>Machines</span>
              <ButtonRed onClick={openAddMachineDialogHandler}>New Machine</ButtonRed>
            </Grid>
          </Grid>
        </Grid>

        {machines && machines.length > 0 ? (
          <Grid container rowGap={2} marginTop={1.5}>
            {machines.map((machine) => {
              const isOnline = onlineMacs.some((mac) => normalizeMac(mac) === normalizeMac(machine.mac_id));
              return (
                <Grid item container xs={12} key={machine.id} className={styles.serverContainer}>
                  <Grid item xs={4} className={styles.serverInfo}>
                    <MonitorIcon />
                    <span className={styles.label}>
                      Machine name: {machine.machine_name} (MAC: {machine.mac_id})
                    </span>
                  </Grid>
                  <Grid item xs={1}>
                    <div className={styles.verticalDivider}></div>
                  </Grid>
                  <Grid item xs={3}>
                    <span className={styles.label}>Status</span>
                    <StatusBadge status={isOnline} />
                  </Grid>
                  <Grid item xs={4} container justifyContent="flex-end" alignItems="center">
                    {isOnline && (
                      <ButtonRed
                        onClick={() => handleSyncMachine(machine)}
                        style={{ marginRight: "10px", padding: "5px 15px", fontSize: "12px" }}
                      >
                        Sync
                      </ButtonRed>
                    )}
                    <IconButton
                      size="small"
                      aria-label="edit"
                      onClick={() => handleEditMachine(machine)}
                      style={{ marginRight: "5px" }}
                    >
                      <EditIcon className={styles.actionIcon} />
                    </IconButton>
                    <IconButton size="small" aria-label="delete" onClick={() => handleDeleteMachine(machine)}>
                      <TrashIcon className={styles.actionIcon} />
                    </IconButton>
                  </Grid>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <WarningMsg title="No Machines" message={`You don't have a machine, please add`} />
        )}
      </div>
      <AddLabelScalePopup open={addMachine} onClose={closeAddMachineDialogHandler} machineToEdit={editingMachine} />
      <ConfirmPopup
        data={machineToDelete}
        handleClose={() => setMachineToDelete(null)}
        handleOkay={onConfirmDeleteMachine}
        alertTitle="Delete Machine"
        alertContent={`Are you sure you want to delete machine '${machineToDelete?.machine_name}'?`}
        successBtnName="Delete"
      />
    </>
  );
}

export default LabelScale;
